import { Fragment, useEffect, useMemo } from 'react';

import {
  FilterAppliedProps,
  FilterConfig,
  FilterMenuProps,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconQuestion } from '@components/icons/question';
import { IconSelectMultiple } from '@components/icons/select-multiple';
import { IconSelectSingle } from '@components/icons/select-single';

import { useStore } from 'models/Provider';
import { JobQuestions, JobQuestionType } from 'types/jobs';
import { ITODO } from 'types/todo';

import { FilterActionButtons } from './filter-action-buttons';

const FILTERABLE_TYPES: Array<JobQuestionType> = ['dropdown', 'multidropdown'];

type ExtraProps = {
  questions: JobQuestions;
};

function FilterApplicationQuestionApplied({ questions, data }: FilterAppliedProps<ExtraProps>) {
  const question = questions.find((q) => q.id === data.field)!;
  const option = question.options.find((o) => o.value === data.value);

  return (
    <Filters.AppliedFilter
      icon={<IconQuestion />}
      category={question.title!}
      value={option?.display!}
    />
  );
}

function FilterApplicationQuestionMenu({ questions }: FilterMenuProps<ExtraProps>) {
  const { t } = useStore();
  const { state, actions, isMultiComparer, hasValue } = useCurrentFilter();

  useEffect(() => {
    if (!state.comparer) {
      actions.selectComparer('is');
    }
  }, [actions, state.comparer]);

  const fieldItems = questions.map((item) => ({
    value: item.id,
    label: item.title,
    type: item.type,
  }));

  let optionItems: Array<{ label: string; value: string }> = [];

  const question = questions.find((question) => question.id === state.field);

  if (question) {
    optionItems = question.options.map((opt) => ({
      label: opt.display,
      value: opt.value,
    }));
  }

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.filters.question.label')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />

      {question && (
        <Filters.ListItem
          heading
          label={question.title}
          icon={<IconArrowLeft />}
          onClick={actions.unselectField}
        />
      )}

      <Filters.ListSeparator />

      {!question && (
        <Fragment>
          {fieldItems.map((item) => (
            <Filters.ListItem
              key={item.value}
              icon={item.type === 'dropdown' ? <IconSelectSingle /> : <IconSelectMultiple />}
              label={item.label}
              onClick={() => {
                actions.selectField(item.value);
              }}
            />
          ))}
        </Fragment>
      )}

      {question && state.comparer && (
        <Fragment>
          <Filters.FilterValueMenu
            items={optionItems}
            isMulti={isMultiComparer}
            onSetValue={actions.selectValue}
            state={state}
          />
          <FilterActionButtons disableApply={!hasValue} />
        </Fragment>
      )}
    </Filters.List>
  );
}

export function useFilterApplicationQuestion(job: ITODO): FilterConfig<ExtraProps> | null {
  const { t } = useStore();

  return useMemo(() => {
    const questions = ((job.questions || []) as JobQuestions).filter((question) =>
      FILTERABLE_TYPES.includes(question.type),
    );
    if (questions.length === 0) return null;
    return {
      category: 'question',
      label: t('applicants.filters.question.label'),
      icon: <IconQuestion />,
      AppliedFilter: FilterApplicationQuestionApplied,
      Menu: FilterApplicationQuestionMenu,
      extraProps: {
        questions,
      },
      toQueryData: (data) => {
        switch (data.comparer) {
          case 'is':
            return { question_filter: `${data.field}__${data.value}` };
          default:
            return {};
        }
      },
    };
  }, [job.questions, t]);
}
